/**
 * Procurement import API client: preview a sheet, download the template.
 */
import { apiCall } from '../api';
import type { ImportPreview, RecentSheet } from '@/types/procurementImport';
import { downloadBlob, getFilenameFromDisposition } from './procurement';

async function throwIfNotOk(response: Response, fallback: string) {
  if (response.ok) return;
  const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
  throw new Error(error.error || `${fallback} (status ${response.status})`);
}

export async function previewImportFromFile(file: File, weekStartDate: string, tab?: string): Promise<ImportPreview> {
  const form = new FormData();
  form.set('file', file, file.name);
  form.set('weekStartDate', weekStartDate);
  if (tab) form.set('tab', tab);

  // No Content-Type header: the browser sets the multipart boundary.
  const response = await fetch('/api/procurement/import/preview', { method: 'POST', credentials: 'include', body: form });
  await throwIfNotOk(response, 'Failed to read the spreadsheet');
  return response.json();
}

export async function previewImportFromUrl(sheetUrl: string, weekStartDate: string, tab?: string): Promise<ImportPreview> {
  return apiCall<ImportPreview>('/api/procurement/import/preview', {
    method: 'POST',
    body: JSON.stringify(tab ? { sheetUrl, weekStartDate, tab } : { sheetUrl, weekStartDate }),
  });
}

export async function downloadImportTemplate(weekStartDate: string): Promise<void> {
  const params = new URLSearchParams({ weekStartDate });
  const response = await fetch(`/api/procurement/import/template?${params}`, { method: 'GET', credentials: 'include' });
  await throwIfNotOk(response, 'Failed to download the template');
  const blob = await response.blob();
  downloadBlob(blob, getFilenameFromDisposition(response.headers.get('Content-Disposition'), `fmb-grocery-template-${weekStartDate}.xlsx`));
}

export async function fetchRecentSheets(): Promise<RecentSheet[]> {
  const { sheets } = await apiCall<{ sheets: RecentSheet[] }>('/api/procurement/import/recent-sheets');
  return sheets;
}

export async function rememberRecentSheet(sheet: Pick<RecentSheet, 'sheetId' | 'url' | 'title'>): Promise<RecentSheet[]> {
  const { sheets } = await apiCall<{ sheets: RecentSheet[] }>('/api/procurement/import/recent-sheets', {
    method: 'POST',
    body: JSON.stringify(sheet),
  });
  return sheets;
}

/** Remember which ingredient the caterer picked for each sheet name, for the next import. */
export async function rememberImportAliases(entries: { rawName: string; ingredientId: number }[]): Promise<number> {
  if (entries.length === 0) return 0;
  const { remembered } = await apiCall<{ remembered: number }>('/api/procurement/import/aliases', {
    method: 'POST',
    body: JSON.stringify({ entries }),
  });
  return remembered;
}

export async function forgetRecentSheet(sheetId: string): Promise<RecentSheet[]> {
  const { sheets } = await apiCall<{ sheets: RecentSheet[] }>(`/api/procurement/import/recent-sheets/${encodeURIComponent(sheetId)}`, { method: 'DELETE' });
  return sheets;
}
